// The palette's other half: the things Sam works with, by name. A workflow
// (opens it; "Run X…" starts it where tasks are on; "Last run of X" opens its
// report), the newest runs and tasks (found by their title and what they ran
// on), a project, a connection and a deployment. All search-only: the empty
// list stays short. Pure, so the navigation self-test can check it; the reads
// are JumpPalette's, and only while it is open.

import { Boxes, ClipboardList, Globe, History, Play, Plug, Workflow } from "lucide-react";

import type { DirectoryEntry } from "@/api/connectionDirectory";
import type { DeploymentRow } from "@/api/deployments";
import type { RunSummary, WorkflowSummary } from "@/api/workflows";
import type { LatticeProject } from "@/api/projects";
import type { TaskSummary } from "@/api/tasks";
import { taskStatusWord } from "@/features/tasks/taskModel";
import { keySetting, runOpenHref, runWord } from "@/features/workflows/list/runFilters";
import { deploymentPath, workflowEditPath, workflowRunPath } from "@/features/workflows/paths";
import { isLiveRun } from "@/features/workflows/status";

import type { JumpItem } from "./jumpItems";

export interface JumpObjects {
  workflows?: readonly WorkflowSummary[];
  runs?: readonly RunSummary[];
  tasks?: readonly TaskSummary[];
  projects?: readonly Pick<LatticeProject, "id" | "name">[];
  connections?: readonly Pick<DirectoryEntry, "id" | "name" | "names" | "host" | "kind_word">[];
  deployments?: readonly Pick<DeploymentRow, "name" | "title" | "state">[];
  /** "Run X…" opens the one start dialog where tasks are on, else the workflow with its run form. */
  /** "Tue 3 Nov" for a stamp; the palette's own clock and zone. */
  day?: (iso: string) => string;
}

const BASE = "/workflows";

/** Every string a run was given, flattened, so a run is found by its question or its product. */
function inputWords(params: RunSummary["params"]): string {
  const out: string[] = [];
  const walk = (v: unknown) => {
    if (typeof v === "string") out.push(v);
    else if (typeof v === "number") out.push(String(v));
    else if (Array.isArray(v)) v.forEach(walk);
  };
  Object.values(params ?? {}).forEach(walk);
  return out.join(" ").slice(0, 2000);
}

function runHint(run: RunSummary, day: (iso: string) => string): string {
  const stamp = run.ended_at || run.started_at || run.created_at;
  // A Team task's parameters are the engine's (its copies), not what anyone asked.
  const asked = run.job ? "" : keySetting(run.params ?? {});
  return [runWord(run).label, stamp ? day(stamp) : "", asked].filter(Boolean).join(" · ");
}

/** A finished run opens its report; a live one, or a Team task, the page that shows it now. */
function lastRunHref(run: RunSummary): string {
  return run.job || isLiveRun(run.status) ? runOpenHref(BASE, run) : `${workflowRunPath(BASE, run.run_id)}/report`;
}

export function buildObjectItems({ workflows = [], runs = [], tasks = [], projects = [], connections = [], deployments = [], day = (s) => s.slice(0, 10) }: JumpObjects): JumpItem[] {
  const named: JumpItem[] = workflows.flatMap((w) => {
    const last = w.last_run;
    const rows: JumpItem[] = [
      {
        id: `workflow:${w.slug}`,
        group: "Workflows",
        label: w.name,
        hint: last ? runHint(last, day).split(" · ").slice(0, 2).join(" · ") : "Not run yet",
        to: workflowEditPath(BASE, w.slug),
        icon: Workflow,
        keywords: `workflow ${w.slug}`,
        searchOnly: true,
      },
    ];
    rows.push({
      id: `workflow-run:${w.slug}`,
      group: "Workflows",
      label: `Run ${w.name}…`,
      // The small Run dialog, with its copies line and warning: one form for running a workflow.
      to: workflowEditPath(BASE, w.slug),
      run: { slug: w.slug, name: w.name },
      icon: Play,
      keywords: "start",
      searchOnly: true,
    });
    if (last) rows.push({ id: `workflow-last:${w.slug}`, group: "Workflows", label: `Last run of ${w.name}`, hint: runWord(last).label, to: lastRunHref(last), icon: History, keywords: "report result", searchOnly: true });
    return rows;
  });

  // A task and the run behind it are one row (they share an id), the task's title winning.
  const runById = new Map(runs.map((r) => [r.run_id, r]));
  const taskIds = new Set(tasks.map((t) => t.task_id));
  const recent: JumpItem[] = [
    ...runs.filter((r) => !taskIds.has(r.run_id)).map((r) => ({
      at: r.created_at,
      item: {
        id: `run:${r.run_id}`,
        group: "Recent" as const,
        label: r.workflow_name || r.workflow,
        hint: runHint(r, day),
        to: runOpenHref(BASE, r),
        icon: History,
        keywords: `run ${inputWords(r.params)}`,
        searchOnly: true,
      } satisfies JumpItem,
    })),
    ...tasks.map((t) => ({
      at: t.created_at,
      item: {
        id: `task:${t.task_id}`,
        group: "Recent" as const,
        label: t.title,
        hint: [taskStatusWord(t.status).label, t.worker?.name, t.created_at ? day(t.created_at) : ""].filter(Boolean).join(" · "),
        to: `/tasks/${encodeURIComponent(t.task_id)}`,
        icon: ClipboardList,
        keywords: `task ${inputWords(runById.get(t.task_id)?.params)}`,
        searchOnly: true,
      } satisfies JumpItem,
    })),
  ]
    .sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""))
    .slice(0, 20)
    .map((x) => x.item);

  const places: JumpItem[] = [
    ...projects.map((p) => ({ id: `project:${p.id}`, group: "Projects" as const, label: p.name, hint: "Project", to: `/projects?project=${encodeURIComponent(p.id)}`, icon: Boxes, searchOnly: true })),
    ...connections.map((c) => ({
      id: `connection:${c.id}`,
      group: "Connections" as const,
      label: c.name,
      hint: c.kind_word || "Connection",
      to: `/settings?connection=${encodeURIComponent(c.id)}#connections`,
      icon: Plug,
      keywords: [...(c.names ?? []), c.host].filter(Boolean).join(" "),
      searchOnly: true,
    })),
    ...deployments.map((d) => ({
      id: `deployment:${d.name}`,
      group: "Deployments" as const,
      label: d.title || d.name,
      hint: d.state === "live" ? "Deployment" : "Undeployed",
      to: deploymentPath(BASE, d.name),
      icon: Globe,
      keywords: d.name,
      searchOnly: true,
    })),
  ];

  return [...named, ...recent, ...places];
}
